// History field options for AdminPromptBuilder
// Derived from the live documentDataFields configuration

// Category order for display (New first to surface uncategorized fields)
export const CATEGORY_ORDER = [
  'New',
  'Patient Data',
  'Histories',
  'History Summaries',
  'Assessments',
  'Treatment & Therapy',
  'Session & Visit',
  'Clinical Course',
  'Testing & Measures',
  'Referral & Consultation',
];

// Group all visible configured fields; there is no numeric option limit.
// Returns array of { category, fields: [{ value, label, fieldName }] } grouped by category
export const buildHistoryFieldOptions = (dataFields) => {
  const fields = dataFields.filter(field => field.showInHistoryFields !== false);

  // Group by category
  const byCategory = Object.create(null);
  for (const field of fields) {
    const category = field.category || 'New';
    if (!byCategory[category]) {
      byCategory[category] = [];
    }
    byCategory[category].push({
      value: field.id,
      label: field.displayName || field.name || field.fieldName || field.id,
      fieldName: field.fieldName || field.id  // Include fieldName for ID→fieldName lookup
    });
  }

  // Sort fields alphabetically within each category
  for (const category of Object.keys(byCategory)) {
    byCategory[category].sort((a, b) => a.label.localeCompare(b.label));
  }

  // Build ordered result array
  const result = [];
  for (const category of CATEGORY_ORDER) {
    if (byCategory[category] && byCategory[category].length > 0) {
      result.push({
        category,
        fields: byCategory[category]
      });
    }
  }

  // Add any categories not in CATEGORY_ORDER
  for (const category of Object.keys(byCategory)) {
    if (!CATEGORY_ORDER.includes(category)) {
      result.push({
        category,
        fields: byCategory[category]
      });
    }
  }

  return result;
};

// Helper to get all field values as a flat array (from dynamic options)
export const getAllFieldValues = (historyFieldOptions) => {
  return historyFieldOptions.flatMap(category =>
    category.fields.map(field => field.value)
  );
};

// Helper to get label for a field value (from dynamic options)
export const getFieldLabel = (value, historyFieldOptions) => {
  for (const category of historyFieldOptions) {
    const field = category.fields.find(f => f.value === value);
    if (field) return field.label;
  }
  return value; // fallback to value if not found
};

// Helper to build ID → fieldName mapping from history field options
// Used to translate selected Firestore IDs to the actual field keys in patient data
export const buildFieldNameMapping = (historyFieldOptions) => {
  const mapping = {};
  for (const category of historyFieldOptions) {
    for (const field of category.fields) {
      mapping[field.value] = field.fieldName || field.value;
    }
  }
  return mapping;
};

// Helper to resolve an array of field IDs to their fieldNames
export const resolveFieldIds = (fieldIds, historyFieldOptions) => {
  const mapping = buildFieldNameMapping(historyFieldOptions);
  return fieldIds.map(id => mapping[id] || id);
};
